import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from '@mui/material';
import DonationPI from './DonationPI';
import Billing from './Billing';
import DonationInfo from './DonationInfo';
import { useSearchParams } from 'react-router-dom';
import { getInProgressDonation } from '../../../endpoints/donations';
import { useApiGet } from '../../../utils/httpClient';
import { ensureRecaptchaLoaded } from '../../../utils/recaptcha';

const Spacer = () => {
  return <Grid item xs={0} sm={1} md={2} lg={3} />;
};

export default function Donations() {
  let [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get('id');

  const updateSearchParams = useCallback(
    (key, value) => {
      if (value || value === 0) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [activeStep, setActiveStep] = useState(
    searchParams.get('step') ? parseInt(searchParams.get('step'), 10) : 0
  );
  const [step1Complete, setStep1Complete] = useState(false);
  const [step2Complete, setStep2Complete] = useState(false);
  const [step3Complete, setStep3Complete] = useState(false);
  const [step4Complete, setStep4Complete] = useState(false);
  const [donationId, setDonationId] = useState(null);
  const [donationObject, setDonationObject] = useState(null);

  const { data: donationData, refetch } = useApiGet(
    ['donation', donationId],
    getInProgressDonation,
    { enabled: donationId ? true : false },
    { id: donationId }
  );

  useEffect(() => {
    ensureRecaptchaLoaded().catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.has('step')) {
      setActiveStep(parseInt(searchParams.get('step'), 10));
    } else {
      setActiveStep(0);
    }
  }, [searchParams]);

  useEffect(() => {
    updateSearchParams('step', activeStep);
  }, [activeStep, updateSearchParams]);

  useEffect(() => {
    updateSearchParams('id', donationId);
  }, [donationId, updateSearchParams]);

  useEffect(() => {
    if (id) {
      setDonationId(id);
    }
  }, [id]);

  useEffect(() => {
    if (donationData) {
      setDonationObject(donationData);
    }
  }, [donationData]);

  const steps = useMemo(
    () => [
      { name: 'Donor Information', state: step1Complete, step: 1 },
      { name: 'Donation Details', state: step2Complete, step: 2 },
      { name: 'Payment', state: step3Complete, step: 3 },
      { name: 'Finish', state: step4Complete, step: 4 },
    ],
    [step1Complete, step2Complete, step3Complete, step4Complete]
  );

  const handleNext = () => {
    switch (activeStep) {
      case 0:
        setStep1Complete(true);
        break;
      case 1:
        setStep2Complete(true);
        break;
      case 2:
        setStep3Complete(true);
        break;
      case 3:
        setStep4Complete(true);
        break;
      default:
        break;
    }
    if (activeStep >= steps.length - 1) {
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    switch (activeStep) {
      case 1:
        setStep1Complete(false);
        break;
      case 2:
        setStep2Complete(false);
        break;
      case 3:
        setStep3Complete(false);
        break;
      default:
        break;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const activeScreen = () => {
    switch (activeStep) {
      case 0:
        return (
          <DonationPI
            handleNext={handleNext}
            refetch={refetch}
            donationId={donationId}
            donationObject={donationObject}
            setDonationId={setDonationId}
          />
        );
      case 1:
        return (
          <DonationInfo
            donationId={donationId}
            refetch={refetch}
            handleBack={handleBack}
            donationObject={donationObject}
            handleNext={handleNext}
          />
        );
      case 2:
        return (
          <Billing
            donationId={donationId}
            step={activeStep}
            refetch={refetch}
            donationObject={donationObject}
            handleBack={handleBack}
          />
        );
      case 3:
        return (
          <Billing
            donationId={donationId}
            step={activeStep}
            refetch={refetch}
            donationObject={donationObject}
            handleBack={handleBack}
          />
        );
      default:
        return <DonationPI />;
    }
  };

  return (
    <Box
      component="main"
      sx={{ minHeight: '85vh' }}
      aria-label="Donation checkout"
    >
      <Grid container className="backgroundImage">
        <Grid item xs={12} sx={{ pt: { xs: 2, sm: 4 } }} />
        <Grid item container xs={12} justifyContent="center">
          <Spacer />
          <Grid
            item
            xs={12}
            sm={10}
            md={8}
            lg={6}
            sx={{ px: { xs: 1, sm: 2 } }}
          >
            <Paper
              elevation={4}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Container
                maxWidth="md"
                disableGutters
                sx={{ py: 2, px: { xs: 1.5, sm: 3 } }}
              >
                <Typography
                  variant="h5"
                  component="h1"
                  gutterBottom
                  sx={{ fontWeight: 600, textAlign: 'center' }}
                >
                  Make a donation
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', mb: 2 }}
                >
                  Your support helps chapters and projects across the
                  organization. All fields marked required must be completed
                  before payment.
                </Typography>
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  sx={{
                    py: 2,
                    '& .MuiStepLabel-label': {
                      typography: 'caption',
                      fontWeight: 500,
                    },
                  }}
                >
                  {steps.map((step) => (
                    <Step key={step.name} completed={step.state}>
                      <StepLabel>{step.name}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <Box sx={{ pt: 1 }}>{activeScreen()}</Box>
              </Container>
            </Paper>
          </Grid>
          <Spacer />
        </Grid>
        <Grid item xs={12} sx={{ pb: { xs: 4, sm: 6 } }} />
      </Grid>
    </Box>
  );
}
